function padDateTimeSegment(value) {
  return String(value).padStart(2, "0");
}

function emptyAdvertTable() {
  return { headers: [], rows: [] };
}

function buildNormalizedAdvertTable(item) {
  return item?.table || emptyAdvertTable();
}

function buildNormalizedAdvertContentBase(item) {
  return {
    title: item?.title || "",
    type: item?.type || "text",
    content: item?.content || "",
    table: buildNormalizedAdvertTable(item),
  };
}

function buildDisplayOrderItem(index) {
  return {
    key: `section:${index}`,
    source: "section",
    index,
  };
}

function buildAdvertSummary(advert) {
  return {
    id: advert.id,
    title: advert.title || "",
    reference_number: advert.reference_number || "",
    details: advert.details || "",
  };
}

function buildAdvertDates(advert, formatDateInput) {
  return {
    published_date: formatDateInput(advert.published_date),
    closing_date: formatDateInput(advert.closing_date),
  };
}

function buildDisplayFallbackItems(hasDetails, sections) {
  const items = hasDetails ? [{ key: "details", source: "details" }] : [];
  (sections || []).forEach((_, idx) => { items.push(buildDisplayOrderItem(idx)); });
  return items;
}

function hasVisibleTableCell(row, advertStripHtmlTags) {
  return Array.isArray(row) && row.some((cell) => !!advertStripHtmlTags(cell || ""));
}

function escapeAdvertHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildAdvertHtmlMarkup(str) {
  return String(str)
    .split(/\r?\n/)
    .reduce((html, line, index) => `${html}${index ? "<br>" : ""}${escapeAdvertHtml(line)}`, "");
}

function canPushDisplayItem(target, item, allowed = null) {
  if (!item) return false;
  if (allowed && !allowed.has(item.key)) return false;
  return !target.some((entry) => entry.key === item.key);
}

function pushUniqueDisplayItems(target, items, allowed = null) {
  items.forEach((item) => {
    if (!canPushDisplayItem(target, item, allowed)) return;
    target.push(item);
  });
}

function buildDisplayBlockBase(key, kind, title, data) {
  return { key, kind, title, data };
}

export default {
  computed: {
    editAdvertMinimumDateTime() {
      const now = new Date();
      return `${now.getFullYear()}-${padDateTimeSegment(now.getMonth() + 1)}-${padDateTimeSegment(now.getDate())}T${padDateTimeSegment(now.getHours())}:${padDateTimeSegment(now.getMinutes())}`;
    },
    editAdvertClosingMinimumDateTime() {
      return this.editAdvertData.published_date || this.editAdvertMinimumDateTime;
    },
  },
  methods: {
    buildViewAdvertData() {
      return {
        id: null,
        title: "",
        reference_number: "",
        published_date: "",
        closing_date: "",
        vacancies: null,
        details: "",
        sections: [],
        job_profile_title: "",
        job_profile_description: "",
        job_profile_sections: [],
        display_order: [],
        display_blocks: [],
      };
    },
    normalizeAdvertSubsections(subsections) {
      return Array.isArray(subsections)
        ? subsections.map((subsection) => this.normalizeAdvertSubsection(subsection))
        : [];
    },
    normalizeAdvertSubsection(subsection) {
      return buildNormalizedAdvertContentBase(subsection);
    },
    normalizeAdvertSection(section) {
      return {
        ...buildNormalizedAdvertContentBase(section),
        subsections: this.normalizeAdvertSubsections(section?.subsections),
      };
    },
    buildEmptyEditData() {
      return {
        id: null,
        title: "",
        reference_number: "",
        published_date: "",
        closing_date: "",
        vacancies: null,
        details: "",
        sections: [],
        job_profile_title: "",
        job_profile_description: "",
        job_profile_sections: [],
        display_order: [],
      };
    },
    splitAdvertSectionsAndOrder(rawSections) {
      const list = Array.isArray(rawSections) ? JSON.parse(JSON.stringify(rawSections)) : [];
      const meta = list.find(item => item && item.type === "__display_order_meta__" && Array.isArray(item.items));
      return {
        contentSections: list.filter(item => !(item && item.type === "__display_order_meta__")),
        orderItems: meta?.items || [],
      };
    },
    normalizeDisplayOrder(savedOrder, hasDetails, sections) {
      const fallback = buildDisplayFallbackItems(hasDetails, sections);
      const allowed = new Set(fallback.map(item => item.key));
      const normalized = [];
      pushUniqueDisplayItems(normalized, Array.isArray(savedOrder) ? savedOrder : [], allowed);
      pushUniqueDisplayItems(normalized, fallback);
      return normalized;
    },
    formatDateInput(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return `${date.getFullYear()}-${padDateTimeSegment(date.getMonth() + 1)}-${padDateTimeSegment(date.getDate())}T${padDateTimeSegment(date.getHours())}:${padDateTimeSegment(date.getMinutes())}`;
    },
    advertHasDetails(advert) {
      return !!(advert.details && String(advert.details).trim());
    },
    buildNormalizedEditSections(split) {
      return (split.contentSections || []).map((section) => this.normalizeAdvertSection(section));
    },
    buildEditDisplayOrder(advert, split) {
      return this.normalizeDisplayOrder(
        split.orderItems,
        this.advertHasDetails(advert),
        split.contentSections
      );
    },
    buildEditAdvertMetadata(advert) {
      return {
        ...buildAdvertSummary(advert),
        ...buildAdvertDates(advert, this.formatDateInput),
        vacancies: advert.vacancies || null,
      };
    },
    buildEditDataFromAdvert(advert, split) {
      return {
        ...this.buildEditAdvertMetadata(advert),
        sections: this.buildNormalizedEditSections(split),
        job_profile_title: "",
        job_profile_description: "",
        job_profile_sections: [],
        display_order: this.buildEditDisplayOrder(advert, split),
      };
    },
    getJobProfilePayload(responseData) {
      return responseData?.data || responseData || {};
    },
    getVisibleJobProfileSections(payload) {
      const sections = Array.isArray(payload.sections) ? payload.sections : [];
      return sections.filter((section) => section.show_on_advert !== false);
    },
    applyJobProfilePayload(responseData) {
      const payload = this.getJobProfilePayload(responseData);
      this.profileSections = this.getVisibleJobProfileSections(payload);
      this.viewAdvertData.job_profile_title = payload.job_title || payload.title || "";
      this.viewAdvertData.job_profile_description = payload.description || "";
      this.viewAdvertData.job_profile_sections = this.profileSections;
      this.editAdvertData.job_profile_title = this.viewAdvertData.job_profile_title;
      this.editAdvertData.job_profile_description = this.viewAdvertData.job_profile_description;
      this.editAdvertData.job_profile_sections = this.profileSections;
    },
    canFetchLatestAdvert(selectedAdvert) {
      return !!(Number(selectedAdvert?.id) && Number(selectedAdvert?.entity_type_id));
    },
    findLatestAdvert(rows, advertId) {
      return (Array.isArray(rows) ? rows : []).find((row) => Number(row?.id) === advertId) || null;
    },
    buildDisplayBlocks(advert) {
      const blocks = [];
      for (const item of advert.display_order || []) {
        const block = this.buildDisplayBlock(advert, item);
        if (block) blocks.push(block);
      }
      return blocks;
    },
    buildDisplayBlock(advert, item) {
      if (!item) return null;
      if (item.source === "details") return this.buildDetailsDisplayBlock(advert, item);
      if (item.source === "section") return this.buildSectionDisplayBlock(advert, item);
      return null;
    },
    buildDetailsDisplayBlock(advert, item) {
      const text = advert.details || "";
      if (!String(text).trim()) return null;
      return buildDisplayBlockBase(item.key || "details", "details", "Additional Information", text);
    },
    getAdvertSectionByIndex(sections, index) {
      return Number.isInteger(index) ? Reflect.get(sections || [], index) : null;
    },
    buildSectionDisplayBlockTitle(section, index) {
      return section.title || section.heading || `Section ${index + 1}`;
    },
    buildSectionDisplayBlock(advert, item) {
      const index = Number(item.index);
      const section = this.getAdvertSectionByIndex(advert.sections, index);
      if (!section) return null;
      return buildDisplayBlockBase(
        item.key || `section:${index}`,
        "section",
        this.buildSectionDisplayBlockTitle(section, index),
        section
      );
    },
    advertCleanHtml(value) {
      if (!value) return "";
      const str = String(value);
      if (/<[a-z][\s\S]*>/i.test(str)) return str;
      return buildAdvertHtmlMarkup(str);
    },
    advertStripHtmlTags(text) {
      if (!text) return "";
      return String(text).replace(/<[^>]+>/g, "").trim();
    },
    advertHasTableContent(section) {
      const rows = Array.isArray(section?.table?.rows) ? section.table.rows : [];
      return rows.some((row) => hasVisibleTableCell(row, (cell) => this.advertStripHtmlTags(cell)));
    },
  },
};
